import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import { RichBox } from './CustomerPage.jsx';
import { CUSTOMER_STATUSES, fmtDate } from './ws.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

const PIPE_COLORS = { prospect: '#cbd5e1', contacted: '#93c5fd', negotiating: '#fbbf24', won: '#22c55e', lost: '#f87171' };

// Stats per user: customers, messages, emails, documents sent / accepted,
// quotes won, and each user's pipeline (Owner / Admin / manager: everyone).
export function Stats({ title }) {
  const { t, errorText } = useI18n();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api(`/workspace/stats?days=${days}`).then(setData).catch((e) => setError(errorText(e))); }, [days, errorText]);
  const cols = ['customers', 'messages', 'emails', 'docsSent', 'docsAccepted', 'quotesWon', 'won'];
  return (
    <div className="ws-page">
      <div className="acc-head"><TutorialSlot place="ws.stats" /><div><h1>{title || t('ws.stats.title')}</h1><p className="muted">{t('ws.stats.lead')}</p></div></div>
      <Alert>{error}</Alert>
      <div className="acc-toolbar"><select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t('ws.stats.period')}>{[7, 30, 90, 365].map((n) => <option key={n} value={n}>{t('ws.stats.days', { n })}</option>)}</select></div>
      {data && (
        <div className="ws-table-wrap">
          <table className="ws-table ws-table-cards">
            <thead><tr><th>{t('ws.stats.user')}</th>{cols.map((c) => <th key={c}>{t(`ws.stats.${c}`)}</th>)}<th>{t('ws.stats.pipeline')}</th></tr></thead>
            <tbody>
              {data.rows.map((r) => {
                const total = CUSTOMER_STATUSES.reduce((n, s) => n + r.pipeline[s], 0) || 1;
                return (
                  <tr key={r.id}>
                    <td className="ws-td-main"><strong>{r.name}</strong>{r.title && <span className="muted small"> · {r.title}</span>}</td>
                    {cols.map((c) => <td key={c} data-label={t(`ws.stats.${c}`)}>{r[c]}</td>)}
                    <td data-label={t('ws.stats.pipeline')}>
                      <span className="ws-bars" title={CUSTOMER_STATUSES.map((s) => `${t(`ws.crm.${s}`)}: ${r.pipeline[s]}`).join(' · ')}>
                        {CUSTOMER_STATUSES.map((s) => <span key={s} style={{ width: `${(r.pipeline[s] / total) * 100}%`, background: PIPE_COLORS[s] }} />)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="ws-hint">{CUSTOMER_STATUSES.map((s) => <span key={s} style={{ marginRight: 12 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: PIPE_COLORS[s] }} /> {t(`ws.crm.${s}`)}</span>)}</p>
    </div>
  );
}

// PME representatives: the quote requests (RFQ) assigned to them.
export function RepRequests() {
  const { t, lang, errorText } = useI18n();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/workspace/requests').then(setItems).catch((e) => setError(errorText(e))); }, [errorText]);
  return (
    <div className="ws-page">
      <div className="acc-head"><TutorialSlot place="ws.rep.requests" /><div><h1>{t('ws.req.title')}</h1><p className="muted">{t('ws.req.lead')}</p></div></div>
      <Alert>{error}</Alert>
      {items && (
        <ul className="ws-list ws-card">
          {items.map((r) => (
            <li key={r.id}>
              <span className="ws-grow"><strong>{r.title}</strong> — {r.company}{r.quantity ? ` · ${Number(r.quantity)} ${r.unit || ''}` : ''}</span>
              <span className="ws-badge">{t(`ws.req.status.${r.status}`)}</span>
              <span className="small muted">{fmtDate(r.at, lang)}</span>
              <Link className="btn btn-ghost btn-sm" to={`/account/workspace/documents/new?rfqId=${r.id}&kind=quotation`}>📄 {t('requestsPage.replyDoc')}</Link>
            </li>
          ))}
          {!items.length && <li className="muted">{t('ws.req.empty')}</li>}
        </ul>
      )}
    </div>
  );
}

// "Modèles de courriels": templates for emails to customers (FR/EN), shown
// on the document templates page. Owner / Admin ("Gérer les modèles").
export function EmailTemplates({ canEdit }) {
  const { t, errorText } = useI18n();
  const [mails, setMails] = useState([]);
  const [mail, setMail] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => { api('/workspace/email-templates').then(setMails).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  async function saveMail() {
    setError('');
    try { await api('/workspace/email-templates', { method: 'POST', body: mail }); setMail(null); setMsg(t('ws.tpl.saved')); load(); } catch (e) { setError(errorText(e)); }
  }
  async function delMail(id) { await api(`/workspace/email-templates/${id}`, { method: 'DELETE' }).catch(() => {}); load(); }
  return (
    <section className="ws-card">
      <div className="ws-row"><h2 className="ws-grow">{t('ws.tpl.emails')}</h2>{canEdit && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMail({ name: '', lang: 'fr', subject: '', html: '' })}>+ {t('ws.tpl.newEmail')}</button>}</div>
      <Alert>{error}</Alert>
      {msg && <p className="callout callout-success small" role="status">{msg}</p>}
      <ul className="ws-list">
        {mails.map((m) => <li key={m.id}><span className="ws-grow"><strong>{m.name}</strong> <span className="muted small">({m.lang.toUpperCase()}) — {m.subject}</span></span>
          {canEdit && <><button type="button" className="btn btn-link btn-sm" onClick={() => setMail(m)}>{t('team.edit')}</button><button type="button" className="btn btn-link btn-sm" onClick={() => delMail(m.id)}>✕</button></>}</li>)}
        {!mails.length && <li className="muted small">{t('ws.tpl.noEmails')}</li>}
      </ul>
      {mail && (
        <div className="ws-form">
          <label className="field"><span className="field-label">{t('ws.tpl.emailName')}</span><input value={mail.name} onChange={(e) => setMail({ ...mail, name: e.target.value })} /></label>
          <label className="field"><span className="field-label">{t('ws.docs.lang')}</span><select value={mail.lang} onChange={(e) => setMail({ ...mail, lang: e.target.value })}><option value="fr">Français</option><option value="en">English</option></select></label>
          <label className="field ws-full"><span className="field-label">{t('ws.mail.subject')}</span><input value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} /></label>
          <div className="ws-full"><RichBox value={mail.html} onChange={(v) => setMail({ ...mail, html: v })} placeholder={t('ws.mail.bodyPh')} /></div>
          <p className="ws-hint ws-full">{t('ws.mail.placeholders')}</p>
          <div className="ws-actions ws-full"><button type="button" className="btn btn-ghost" onClick={() => setMail(null)}>{t('common.cancel')}</button><button type="button" className="btn btn-primary" onClick={saveMail}>{t('common.save')}</button></div>
        </div>
      )}
    </section>
  );
}
